import { useEffect, useState, useCallback, useRef } from "react";
import { Navigate } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { ApiService } from "../../services/api";
import { isYesToolsHostname } from "../../utils/yesTools";

interface QrScan {
  type: "entry" | "exit";
  at: string;
}

interface QrStatus {
  hasQr: boolean;
  poolReset?: boolean;
  code: string | null;
  assignedAt: string | null;
  expiresAt: string | null;
  isExpired: boolean;
  entryTime: string | null; // ilk giriş
  exitTime: string | null; // son çıkış
  entryCount?: number;
  exitCount?: number;
  isInside?: boolean;
  scans?: QrScan[];
}

// QR günü: havuz her gece 01:00'da (TR) yenilenir, yeni kod alımı 07:30'da açılır.
// 01:00-07:30 arası ekranda QR gösterilmez, buton pasif kalır.
const NIGHT_START_MINUTES = 1 * 60; // 01:00
const OPEN_MINUTES = 7 * 60 + 30; // 07:30

const getIstanbulMinutes = () => {
  const parts = new Intl.DateTimeFormat("tr-TR", {
    timeZone: "Europe/Istanbul",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(new Date());
  const hour = Number(parts.find((p) => p.type === "hour")?.value ?? 0);
  const minute = Number(parts.find((p) => p.type === "minute")?.value ?? 0);
  return hour * 60 + minute;
};

const isNightWindow = () => {
  const minutes = getIstanbulMinutes();
  return minutes >= NIGHT_START_MINUTES && minutes < OPEN_MINUTES;
};

const formatTime = (iso: string | null) => {
  if (!iso) return null;
  return new Date(iso).toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" });
};

export default function AkademiQrPage() {
  const isYesToolsDomain =
    typeof window !== "undefined" && isYesToolsHostname(window.location.hostname);

  const [status, setStatus] = useState<QrStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [requesting, setRequesting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [requestError, setRequestError] = useState<string | null>(null);
  // Kapı hakkı giriş kartından geliyorsa kalan giriş (kod isteğinin yanıtında gelir).
  const [entryInfo, setEntryInfo] = useState<{ remaining: number; total: number } | null>(null);

  // Havuz gün ortasında yenilenirse (poolReset) elimizdeki kod ölür;
  // kullanıcı o gün zaten QR almışsa yenisini otomatik isteriz.
  const hadQrRef = useRef(false);
  const autoRenewTriedRef = useRef(false);
  // request idempotent: QR varken de sayfa başına bir kez sessizce çağrılır ki
  // ad-soyad muhasebeye işlensin (kod değişmez, isim güncellenir).
  const nameSyncedRef = useRef(false);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await ApiService.getApi("akademi/qr/status");
      setStatus(res?.data || null);
      setError(null);
    } catch (e: any) {
      setError(e?.message || "QR durumu alınamadı.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, 10_000);
    return () => clearInterval(interval);
  }, [fetchStatus]);

  const handleRequest = useCallback(async () => {
    setRequesting(true);
    setRequestError(null);
    try {
      const response: any = await ApiService.postApi("akademi/qr/request", {});
      if (response?.entry) setEntryInfo(response.entry);
      await fetchStatus();
    } catch (e: any) {
      setRequestError(e?.message || "QR oluşturulamadı.");
    } finally {
      setRequesting(false);
    }
  }, [fetchStatus]);

  useEffect(() => {
    if (!status) return;

    if (status.hasQr && status.code) {
      hadQrRef.current = true;
      autoRenewTriedRef.current = false;
      if (!nameSyncedRef.current && !status.isExpired && !isNightWindow()) {
        nameSyncedRef.current = true;
        ApiService.postApi("akademi/qr/request", {})
          .then((response: any) => { if (response?.entry) setEntryInfo(response.entry); })
          .catch(() => {});
      }
      return;
    }

    if (
      status.poolReset &&
      hadQrRef.current &&
      !autoRenewTriedRef.current &&
      !requesting &&
      !isNightWindow()
    ) {
      autoRenewTriedRef.current = true;
      handleRequest();
    }
  }, [status, requesting, handleRequest]);

  if (!isYesToolsDomain) {
    return <Navigate to="/dashboard" replace />;
  }

  const night = isNightWindow();
  const scans = status?.scans || [];
  const hasActiveQr = Boolean(status?.hasQr && !status.isExpired && status.code);

  return (
    <div className="flex flex-col gap-5 p-4 md:p-6 max-w-md mx-auto w-full">
      <div>
        <h1 className="text-xl font-bold text-white">Akademi Günlük QR</h1>
        <p className="text-sm text-white/60 mt-1">
          Günlük QR'ınızla kapıdan gün boyu giriş ve çıkış yapabilirsiniz.
        </p>
      </div>

      {night ? (
        <div className="bg-white border border-gray-200 rounded-xl p-8 flex flex-col items-center gap-4 text-center">
          <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center text-2xl">🌙</div>
          <div>
            <p className="text-sm font-semibold text-gray-800">QR Alımı Henüz Açılmadı</p>
            <p className="text-xs text-gray-400 mt-1">
              Yeni günün QR kodu sabah 07:30'dan itibaren alınabilir.
            </p>
          </div>
          <button
            disabled
            className="px-6 py-2.5 rounded-xl bg-gray-200 text-gray-400 text-sm font-semibold cursor-not-allowed"
          >
            QR Oluştur
          </button>
        </div>
      ) : loading ? (
        <div className="bg-white border border-gray-200 rounded-xl p-10 flex items-center justify-center">
          <span className="text-sm text-gray-400">Yükleniyor...</span>
        </div>
      ) : !status && error ? (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700">
          {error}
        </div>
      ) : hasActiveQr ? (
        <div className="flex flex-col gap-4">
          <HosgeldinKarti />
          <PresenceBadge scans={scans} isInside={status!.isInside} />
          {entryInfo && (
            <div className="rounded-xl border border-blue-100 bg-blue-50 px-4 py-2.5 text-sm text-blue-800">
              Giriş kartında <strong>{entryInfo.remaining}/{entryInfo.total}</strong> giriş kaldı. Bugünün girişi düşüldü; gün içinde istediğin kadar girip çıkabilirsin.
            </div>
          )}

          <div className="bg-white border border-gray-200 rounded-xl p-6 flex flex-col items-center gap-4">
            <div className="p-3 bg-white rounded-lg shadow-sm border border-gray-100">
              <QRCodeSVG value={status!.code!} size={200} level="M" includeMargin={false} />
            </div>
            <div className="text-center">
              <p className="text-xs text-gray-500">
                Gün boyu tüm giriş ve çıkışlarınızda bu kodu okutun.
              </p>
              <p className="text-xs text-gray-400 mt-1">Gece 01:00'a kadar geçerlidir.</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <TimeCard
              label="İlk Giriş"
              time={formatTime(status!.entryTime)}
              color="green"
              emptyText="Henüz Giriş Yapılmadı"
            />
            <TimeCard
              label="Son Çıkış"
              time={formatTime(status!.exitTime)}
              color="blue"
              emptyText="Henüz Çıkış Yapılmadı"
            />
          </div>

          {scans.length > 0 && (
            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-bold uppercase tracking-wide text-gray-500">
                  Bugünün Hareketleri
                </p>
                <p className="text-xs text-gray-400">
                  {status!.entryCount ?? scans.filter((s) => s.type === "entry").length} giriş ·{" "}
                  {status!.exitCount ?? scans.filter((s) => s.type === "exit").length} çıkış
                </p>
              </div>
              <div className="flex flex-col gap-2 max-h-56 overflow-y-auto">
                {scans.map((scan, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <span
                      className={`w-2 h-2 rounded-full shrink-0 ${
                        scan.type === "entry" ? "bg-green-500" : "bg-blue-500"
                      }`}
                    />
                    <span
                      className={`text-sm font-medium ${
                        scan.type === "entry" ? "text-green-700" : "text-blue-700"
                      }`}
                    >
                      {scan.type === "entry" ? "Giriş" : "Çıkış"}
                    </span>
                    <span className="text-sm text-gray-500 ml-auto tabular-nums">
                      {formatTime(scan.at)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-xl p-8 flex flex-col items-center gap-4 text-center">
          <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center text-2xl">📱</div>
          <div>
            <p className="text-sm font-semibold text-gray-800">
              {status?.poolReset ? "QR Havuzu Güncellendi" : "Bugün İçin QR Kodunuz Yok"}
            </p>
            <p className="text-xs text-gray-400 mt-1">
              {status?.poolReset
                ? "Yeni bir QR kodu oluşturabilirsiniz."
                : "Aşağıdaki butona basarak günlük QR kodunuzu oluşturun."}
            </p>
          </div>
          <button
            onClick={handleRequest}
            disabled={requesting}
            className="px-6 py-2.5 rounded-xl bg-blue-700 text-white text-sm font-semibold hover:bg-blue-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {requesting ? "Oluşturuluyor..." : "QR Oluştur"}
          </button>
          {requestError && <p className="text-xs text-red-600">{requestError}</p>}
        </div>
      )}
    </div>
  );
}

function PresenceBadge({ scans, isInside }: { scans: QrScan[]; isInside?: boolean }) {
  if (scans.length === 0) {
    return (
      <div className="rounded-xl border border-gray-200 bg-white px-4 py-3 flex items-center gap-2">
        <span className="w-2.5 h-2.5 rounded-full bg-gray-300" />
        <span className="text-sm font-semibold text-gray-500">Bugün henüz okutma yapılmadı</span>
      </div>
    );
  }

  return isInside ? (
    <div className="rounded-xl border border-green-200 bg-green-50 px-4 py-3 flex items-center gap-2">
      <span className="w-2.5 h-2.5 rounded-full bg-green-500" />
      <span className="text-sm font-semibold text-green-700">Şu an içeridesiniz</span>
    </div>
  ) : (
    <div className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 flex items-center gap-2">
      <span className="w-2.5 h-2.5 rounded-full bg-blue-500" />
      <span className="text-sm font-semibold text-blue-700">Şu an dışarıdasınız</span>
    </div>
  );
}

function TimeCard({
  label,
  time,
  color,
  emptyText,
}: {
  label: string;
  time: string | null;
  color: "green" | "blue";
  emptyText: string;
}) {
  const active = time !== null;
  const colorMap = {
    green: {
      border: active ? "border-green-200" : "border-gray-100",
      bg: active ? "bg-green-50" : "bg-gray-50",
      label: active ? "text-green-700" : "text-gray-400",
      value: "text-green-700",
    },
    blue: {
      border: active ? "border-blue-200" : "border-gray-100",
      bg: active ? "bg-blue-50" : "bg-gray-50",
      label: active ? "text-blue-700" : "text-gray-400",
      value: "text-blue-700",
    },
  };
  const c = colorMap[color];

  return (
    <div className={`rounded-xl border ${c.border} ${c.bg} p-4`}>
      <p className={`text-xs font-bold uppercase tracking-wide ${c.label} mb-2`}>{label}</p>
      {active ? (
        <p className={`text-2xl font-extrabold ${c.value} leading-none`}>{time}</p>
      ) : (
        <p className="text-xs text-gray-400 leading-snug">{emptyText}</p>
      )}
    </div>
  );
}

// Kapıda hoş geldin (25 Eki — sahibin isteği): her girişte WhatsApp yerine burada; planından bugünün işi ve dün bitirdikleri.
// İstek düşerse kart görünmez (QR ekranını asla bloklamaz).
function HosgeldinKarti() {
  const [d, setD] = useState<{ ad: string | null; sonraki: { baslik: string; dakika: number | null } | null; bugunIs: number; dunBiten: number } | null>(null);
  useEffect(() => {
    ApiService.getHosgeldin().then(setD).catch(() => setD(null));
  }, []);
  if (!d) return null;
  return (
    <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
      <p className="font-semibold">Hoş geldin{d.ad ? ` ${d.ad}` : ""}! İyi çalışmalar 👋</p>
      {d.sonraki && (
        <p className="mt-1">
          Bugün planında: <strong>{d.sonraki.baslik}</strong>
          {d.sonraki.dakika ? ` (${d.sonraki.dakika} dk)` : ""}
          {d.bugunIs > 1 ? ` ve ${d.bugunIs - 1} iş daha` : ""}
        </p>
      )}
      {d.dunBiten > 0 && <p className="mt-1 text-emerald-800">Dün {d.dunBiten} iş bitirdin, böyle devam 👏</p>}
    </div>
  );
}
